import { DataContext, SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { ComponentNode } from './component-node.js';
import { provideContainer } from './dependency/container-provider.js';
function fragmentNodeError(message) {
    return new Error(`[Fragment Node]: ${message}`);
}
/**
 * Fragment node representation.
 * It's responsible to handle all stuff related to fragment children state and event.
 */
class FragmentNode {
    constructor(args) {
        this.children = new Map();
        this.root = null;
        this.engine = args.engine;
        this.instanceUUID = args.instanceUUID;
        this.metadata = args.metadata;
        this.name = args.name;
        this.root = args.root;
        this.submittableFragmentContext = args.submittable
            ? new SubmittableFragmentContext()
            : null;
        this.dataContext = new DataContext(args.contextData, args.root?.dataContext);
        this.container = provideContainer({
            dataContext: this.dataContext,
            fragment: this,
            submittableFragmentContext: this.submittableFragmentContext,
        });
    }
    getKey(uuid, instanceUUID) {
        return `${uuid}_${instanceUUID}`;
    }
    /**
     * Discard everything related to the component instance.
     *
     * @param uuid - The component uuid that must be discarded;
     * @param instanceUUID - The component instance uuid;
     * @param parent - The component parent.
     */
    discard(uuid, instanceUUID, parent) {
        const key = this.getKey(uuid, instanceUUID);
        const child = this.children.get(key);
        if (child) {
            child.getAPI().discard();
            child.destroy();
            parent?.removeChild(child);
        }
        this.children.delete(key);
    }
    /**
     * Destroy all the resources created by this instance of fragment.
     */
    destroy() {
        this.dataContext.destroy();
        this.submittableFragmentContext?.destroy();
    }
    /**
     * Retrieves the ComponentNode by the uuid, it only returns a instance when the component is rendered.
     * A Component that is not rendered, does not exists.
     * If the component is @repeated, it's not possible to retrieve any component instance.
     *
     * @param uuid - The child uuid.
     * @returns The ComponentNode instance, or null.
     * @throws If the component is @repeated.
     */
    getChild(uuid) {
        const nodes = Array.from(this.children.values())
            .filter(a => a.getUUID() === uuid);
        if (nodes.length > 1) {
            throw fragmentNodeError('It is not possible to get @repeated components.');
        }
        return nodes[0] || null;
    }
    /**
     * Retrieves the child metadata, by uuid.
     *
     * @param uuid - The child uuid.
     */
    getChildMetadata(uuid) {
        return this.getMetadata().getNode(uuid);
    }
    /**
     * Retrieves the fragment Dependency Injection container.
     */
    getContainer() {
        return this.container;
    }
    /**
     * Retrieves the fragment DataContext instance.
     */
    getDataContext() {
        return this.dataContext;
    }
    /**
     * Retrieves the fragment SubmittableFragmentContext instance.
     */
    getSubmittableFragmentContext() {
        return this.submittableFragmentContext;
    }
    /**
     * Retrieves the Engine instance.
     */
    getEngine() {
        return this.engine;
    }
    /**
     * Retrieve the hashCode of the fragment instance.
     * It's compose by the root's hashCode + it's uuid, recursively.
     */
    getHashCode() {
        // TODO: create hash using the node uuid
        const hashCode = this.instanceUUID;
        if (this.root) {
            const rootHashCode = this.root.getHashCode();
            return `${rootHashCode}_${hashCode}`;
        }
        return hashCode;
    }
    /**
     * Retrieve the fragment instance UUID.
     */
    getInstanceUUID() {
        return this.instanceUUID;
    }
    /**
     * Retrieves the fragment name.
     */
    getName() {
        return this.name;
    }
    /**
     * Retrieves the FragmentMetadata instance.
     */
    getMetadata() {
        return this.metadata;
    }
    /**
     * Get or create a ComponentNode for the uuid specified.
     *
     * @param uuid - The component metadata uuid;
     * @param instanceUUID - The component instance uuid;
     * @param parent - The component parent.
     */
    provideNode(uuid, instanceUUID, parent) {
        const key = this.getKey(uuid, instanceUUID);
        let child = this.children.get(key);
        if (!child) {
            const nodeMetadata = this.getChildMetadata(uuid);
            if (!nodeMetadata) {
                throw fragmentNodeError(`there is not component with uuid '${uuid}, ${instanceUUID}.'`);
            }
            child = new ComponentNode({
                instanceUUID,
                nodeMetadata,
                owner: this,
                uuid,
            });
            this.children.set(key, child);
            parent?.addChild(child);
        }
        return child;
    }
}
export { FragmentNode, };
//# sourceMappingURL=fragment-node.js.map